import { Button } from '@creditornot/cb-button';
import { Restaurants, PaperBag, BookOpenBookmark } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Modal, ModalContent } from '@creditornot/cbt-modal';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import useUser from 'common/hooks/useUser';
import { useOnboardingStepsContext } from 'modules/shared-menu/contexts/OnboardingStepsContext';

const WelcomeText = styled.p`
  margin-block-start: 0;
  margin-block-end: 0;
  ${typographyCss.Body2()}
`;

const GuideWrapper = styled.div`
  display: flex;
  gap: 16px;
  border: 1px solid ${color.border};
  border-radius: 8px;
  padding: 16px;
  margin-block: 16px;
`;

const GuideTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.span<{ disabled?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
  width: 70px;
  /* ignore-color */
  background-color: ${color.bgFillBrand};
  border-radius: 50%;
  padding: 20px;
  fill: ${color.bg};
`;

const GuideTitle = styled.h1`
  margin-block-start: 0;
  margin-block-end: 0;
  ${typographyCss.Heading6()}
`;

const GuideDesc = styled.p`
  margin-block-start: 0;
  margin-block-end: 0;
  color: ${color.textSubdued};
  ${typographyCss.Body3()};
`;

const StyledFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const CloseButton = styled(Button).attrs({ variant: 'secondary', size: 'large' })``;

const StartButton = styled(Button).attrs({ variant: 'primary', size: 'large' })``;

type CustomFooterProps = {
  onClose: () => void;
};

const CustomFooter = ({ onClose }: CustomFooterProps) => {
  const { updateCurrentOnboardingStep } = useOnboardingStepsContext();
  const handleStart = () => {
    updateCurrentOnboardingStep('ONBOARDING_STEP_1_ALL_ITEMS');
  };
  const handleClose = () => {
    onClose();
  };
  return (
    <StyledFooter>
      <CloseButton stretch onClick={handleClose}>
        <FormattedMessage id="listing-manager.onboarding.welcome-modal.skip-for-now" />
      </CloseButton>
      <StartButton stretch onClick={handleStart}>
        <FormattedMessage id="listing-manager.onboarding.welcome-modal.start-tour" />
      </StartButton>
    </StyledFooter>
  );
};

export const WelcomeGuideModal = () => {
  const { updateCurrentOnboardingStep, currentOnboardingStep } = useOnboardingStepsContext();
  const intl = useIntl();
  const onClose = () => {
    updateCurrentOnboardingStep('ONBOARDING_SKIPPED');
  };

  const welcomeGuides = [
    {
      orderIndex: 1,
      title: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.first-step-title' }),
      desc: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.first-step-description' }),
      icon: <Restaurants />,
    },
    {
      orderIndex: 2,
      title: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.second-step-title' }),
      desc: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.second-step-description' }),
      icon: <PaperBag />,
    },
    {
      orderIndex: 3,
      title: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.third-step-title' }),
      desc: intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.third-step-description' }),
      icon: <BookOpenBookmark />,
    },
  ];

  const { isEmployee } = useUser();

  return (
    <Modal
      title={intl.formatMessage({ id: 'listing-manager.onboarding.welcome-modal.title' })}
      open={!currentOnboardingStep && !isEmployee}
      onDismiss={onClose}
      sizePreset={'centered:large'}
      footer={<CustomFooter onClose={onClose} />}
    >
      <ModalContent disablePadding>
        <WelcomeText>
          <FormattedMessage id="listing-manager.onboarding.welcome-modal.description" />
        </WelcomeText>
        {welcomeGuides.map(item => {
          return (
            <GuideWrapper key={item.orderIndex}>
              <IconWrapper>{item.icon}</IconWrapper>
              <GuideTextWrapper>
                <GuideTitle>{item.title}</GuideTitle>
                <GuideDesc>{item.desc}</GuideDesc>
              </GuideTextWrapper>
            </GuideWrapper>
          );
        })}
      </ModalContent>
    </Modal>
  );
};
